import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight, Check, ClipboardCheck, Crown, FileText, Film, Image as ImageIcon, Loader2, Lock,
  Maximize2, Play, Presentation, Sparkles, Timer as TimerIcon, Trophy, X,
} from 'lucide-react';
import { CinegraphicPlayer } from '@/components/CinegraphicPlayer';
import { WatermarkedMedia } from '@/components/WatermarkedMedia';
import { completeStep, fetchReflection } from '@/lib/studyModes';
import { fetchChapterSlides, fetchChapterSummary } from '@/lib/chapterDigest';
import ChapterSummaryView from '@/components/study/ChapterSummaryView';
import SlideDeckViewer from '@/components/study/SlideDeckViewer';
import StudyTimer from '@/components/study/StudyTimer';
import { Hint } from '@/components/ui/hint';
import { HELP } from '@/lib/helpText';

const ICONS = {
  cinegraphic: Film, infographic: ImageIcon, explain: FileText, slides: Presentation,
  flashcards: Sparkles, quiz: Trophy, worksheets: ClipboardCheck, mock: TimerIcon, reflect: Crown,
};

/** Study Buddy's nudge after each step — warm, specific, and free (no LLM call). */
const NUDGE = {
  cinegraphic: 'Wow — the whole chapter in two minutes. Now the infographic will lock the layout of it in your head.',
  infographic: "That one page is the chapter's skeleton. Next up: the summary, where every heading becomes a point you can remember.",
  explain: "You've just read the entire chapter in points. Let's flip the slides — seeing it one idea at a time makes it stick.",
  slides: 'Nice pace! Flashcards next — that is where remembering turns into recall.',
  flashcards: "Recall is warming up. Time to prove it: the mastery quiz is next, and I'm cheering for you.",
  quiz: 'That took courage. The worksheet is next — pen, paper, and the questions a teacher would actually set.',
  worksheets: "Written practice done. If you're up for it, sit the timed mock — it's the rehearsal that removes the fear.",
  mock: "You rehearsed it. Whatever the score, you've already done more than most. One reflection and we're done.",
};

const thumbClass =
  'group relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-white text-left transition-all hover:-translate-y-0.5 hover:shadow-md';

/**
 * GuidedSessionPanel — Exam Mode's daily session.
 *
 * Every step is a THUMBNAIL that opens full screen, so nothing is squeezed into
 * a card; Done only unlocks once the student has actually opened (and, for
 * flashcards / quiz / mock, actually finished) the work; and the 60-minute
 * chapter clock sits beside it with a real eye break at the halfway mark.
 */
export function GuidedSessionPanel({ session, onSessionUpdate }) {
  const navigate = useNavigate();
  const [busyKey, setBusyKey] = useState('');
  const [openKey, setOpenKey] = useState('');
  const [engaged, setEngaged] = useState({});
  const [summary, setSummary] = useState(null);
  const [deck, setDeck] = useState(null);
  const [loadingKey, setLoadingKey] = useState('');
  const [nudge, setNudge] = useState('');
  const [onBreak, setOnBreak] = useState(false);

  const steps = session?.steps || [];
  const active = steps.find(s => s.status !== 'done') || null;
  const subject = session?.subject;
  const topic = session?.topic;

  // Engagement survives a refresh, so a student never loses a tick they earned.
  useEffect(() => {
    if (!session?.session_id) return;
    try {
      setEngaged(JSON.parse(window.localStorage.getItem(`gv_engaged_${session.session_id}`) || '{}'));
    } catch { setEngaged({}); }
  }, [session?.session_id]);

  const markEngaged = useCallback((key) => {
    setEngaged(prev => {
      const next = { ...prev, [key]: true };
      try { window.localStorage.setItem(`gv_engaged_${session.session_id}`, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, [session?.session_id]);

  useEffect(() => {
    if (!session?.session_id || active?.key !== 'reflect' || session.reflection) return;
    fetchReflection(session.session_id)
      .then(d => onSessionUpdate?.({ ...session, reflection: d.reflection }))
      .catch(() => { /* reflection is best-effort */ });
  }, [session, active?.key, onSessionUpdate]);

  const openStep = async (step) => {
    setOpenKey(step.key);
    markEngaged(step.key);
    if ((step.kind === 'summary' || step.kind === 'ai_text') && !summary) {
      setLoadingKey('explain');
      try { setSummary((await fetchChapterSummary(subject, topic)).payload); }
      catch (e) { toast.error(e?.message || "Couldn't open the summary — try again in a moment."); }
      setLoadingKey('');
    }
    if (step.kind === 'slides' && !deck) {
      setLoadingKey('slides');
      try { setDeck((await fetchChapterSlides(subject, topic)).payload); }
      catch (e) { toast.error(e?.message || "Couldn't build the slides — try again in a moment."); }
      setLoadingKey('');
    }
  };

  const markDone = async (key) => {
    setBusyKey(key);
    try {
      const res = await completeStep(session.session_id, key);
      if (!res.verified) { toast.message(res.message); }
      else {
        onSessionUpdate?.({ ...res.session, explanation: res.session.explanation || session.explanation });
        setNudge(NUDGE[key] || '');
        setOpenKey('');
      }
    } catch (e) {
      toast.error(e?.message || 'Could not update the step');
    }
    setBusyKey('');
  };

  const canComplete = (step) => !!engaged[step.key];

  const thumbFor = (step) => {
    const Icon = ICONS[step.key] || Sparkles;
    if (step.kind === 'inline_video' && step.content?.thumbnail_url) {
      return (
        <div className="relative aspect-video bg-slate-900">
          <img src={step.content.thumbnail_url} alt={step.content.title}
            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <Play className="w-5 h-5 text-slate-900" />
            </span>
          </span>
        </div>
      );
    }
    if (step.kind === 'inline_image' && step.content?.file_url) {
      return (
        <div className="relative aspect-video bg-slate-50">
          <img src={step.content.thumbnail_url || step.content.file_url} alt={step.content.title}
            className="w-full h-full object-cover" />
        </div>
      );
    }
    return (
      <div className="relative aspect-video bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center gap-1.5">
        <Icon className="w-7 h-7 text-slate-400" strokeWidth={1.75} />
        <p className="font-heading text-[11px] text-slate-500">
          {step.kind === 'link' ? 'Opens its own page' : 'Tap to open full screen'}
        </p>
      </div>
    );
  };

  const overlay = useMemo(() => {
    const step = steps.find(s => s.key === openKey);
    if (!step) return null;
    const body = (() => {
      if (step.kind === 'inline_video' && step.content?.file_url) {
        return (
          <CinegraphicPlayer src={step.content.file_url} contentId={step.content.content_id}
            poster={step.content.thumbnail_url || null} autoFocus />
        );
      }
      if (step.kind === 'inline_image' && step.content?.file_url) {
        return (
          <WatermarkedMedia className="block">
            <img src={step.content.file_url} alt={step.content.title}
              className="w-full max-h-[80vh] object-contain rounded-2xl bg-white" />
          </WatermarkedMedia>
        );
      }
      if (step.kind === 'summary' || step.kind === 'ai_text') {
        return summary ? (
          <div className="bg-white rounded-3xl p-5 sm:p-7 max-h-[80vh] overflow-y-auto">
            <ChapterSummaryView summary={summary} />
          </div>
        ) : (
          <p className="flex items-center gap-2 text-white/80 font-body text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Laying out every heading of this chapter…
          </p>
        );
      }
      if (step.kind === 'slides') {
        return deck ? <div className="h-[80vh]"><SlideDeckViewer deck={deck} subject={subject} /></div> : (
          <p className="flex items-center gap-2 text-white/80 font-body text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Building your deck…
          </p>
        );
      }
      return null;
    })();
    if (!body) return null;
    return (
      <div className="fixed inset-0 z-[80] bg-slate-950/95 p-3 sm:p-8 overflow-y-auto" data-testid="guided-overlay">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="font-heading text-sm text-white truncate">{step.title} · {topic}</p>
            <button onClick={() => setOpenKey('')} data-testid="guided-overlay-close"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white text-[11px] font-heading hover:bg-white/20">
              <X className="w-3.5 h-3.5" /> Close
            </button>
          </div>
          {body}
          <div className="mt-4 flex justify-end">
            {step.status === 'done' ? (
              <button onClick={() => setOpenKey('')} data-testid={`guided-overlay-close-done-${step.key}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 text-white text-[12px] font-heading hover:bg-white/20">
                <Check className="w-3.5 h-3.5" /> Already ticked — back to my session
              </button>
            ) : (
              <button onClick={() => markDone(step.key)} disabled={busyKey === step.key}
                data-testid={`guided-overlay-done-${step.key}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white text-[12px] font-heading disabled:opacity-50">
                {busyKey === step.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Done — next step
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }, [openKey, steps, summary, deck, subject, topic, busyKey]);

  if (!session) return null;

  return (
    <div className="rounded-3xl border-2 border-fuchsia-100 bg-gradient-to-br from-fuchsia-50/70 via-white to-rose-50/50 p-4 sm:p-5"
      data-testid="guided-session-panel">
      <div className="flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500 to-rose-500 text-white flex items-center justify-center shadow-sm">
          <Crown className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-bold text-slate-900 truncate">Today's guided session · {topic}</p>
          <p className="text-[11px] text-slate-500 font-body">{subject} · open each card full screen, and I'll tick it off</p>
        </div>
        <Hint text={HELP.study.guidedProgress} side="left">
          <span className="text-xs font-heading text-fuchsia-700 bg-white/80 ring-1 ring-fuchsia-100 rounded-full px-2.5 py-1" data-testid="guided-progress">
            {session.progress_pct}%
          </span>
        </Hint>
      </div>

      <div className="mt-3 h-1.5 rounded-full bg-white overflow-hidden ring-1 ring-fuchsia-100">
        <div className="h-full bg-gradient-to-r from-fuchsia-500 to-rose-500 transition-all duration-500"
          style={{ width: `${session.progress_pct}%` }} />
      </div>

      <div className="mt-4 grid lg:grid-cols-[1fr_210px] gap-4">
        {/* steps as thumbnails */}
        <div className="space-y-2.5">
          {steps.map((s, i) => {
            const isActive = active?.key === s.key;
            const done = s.status === 'done';
            const locked = !isActive && !done;
            return (
              <div key={s.key} data-testid={`guided-step-${s.key}`}
                className={`rounded-2xl border p-3 transition-all ${
                  done ? 'border-emerald-100 bg-emerald-50/50'
                    : isActive ? 'border-fuchsia-200 bg-white shadow-sm'
                    : 'border-slate-100 bg-white/60'}`}>
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-heading flex-shrink-0 ${
                    done ? 'bg-emerald-500 text-white' : isActive ? 'bg-fuchsia-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                    {done ? <Check className="w-3.5 h-3.5" /> : locked ? <Lock className="w-3 h-3" /> : i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading text-[13px] font-semibold text-slate-800 truncate">
                      {s.title}{s.optional ? ' · optional' : ''}
                    </p>
                    <p className="text-[11px] text-slate-500 font-body truncate">{s.hint}</p>
                  </div>
                  <div className="flex items-center gap-1.5 w-full sm:w-auto justify-end">
                  {(isActive || done) && s.kind === 'link' && (
                    <button data-testid={`guided-open-${s.key}`}
                      onClick={() => { markEngaged(s.key); navigate(s.route); }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 text-white text-[11px] font-heading hover:bg-slate-800 transition-colors flex-shrink-0">
                      {done ? 'Open again' : 'Open'} <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                  {(isActive || done) && s.kind !== 'link' && s.kind !== 'ai_reflect' && (
                    <button data-testid={`guided-maximize-${s.key}`} onClick={() => openStep(s)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 text-[11px] font-heading hover:border-slate-300 transition-colors flex-shrink-0">
                      {loadingKey === s.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Maximize2 className="w-3 h-3" />}
                      {done ? 'See it again' : 'Full screen'}
                    </button>
                  )}
                  {isActive && (
                    <Hint text={canComplete(s) ? HELP.study.guidedDone : 'Open this step first — then I can tick it off.'}>
                      <button data-testid={`guided-done-${s.key}`} onClick={() => markDone(s.key)}
                        disabled={busyKey === s.key || !canComplete(s)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white text-[11px] font-heading hover:shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
                        {busyKey === s.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Done
                      </button>
                    </Hint>
                  )}
                  </div>
                </div>

                {isActive && s.kind !== 'ai_reflect' && (
                  <button onClick={() => (s.kind === 'link' ? (markEngaged(s.key), navigate(s.route)) : openStep(s))}
                    data-testid={`guided-thumb-${s.key}`} className={`${thumbClass} mt-3`}>
                    {thumbFor(s)}
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900/80 text-white text-[10px] font-heading opacity-0 group-hover:opacity-100 transition-opacity">
                      <Maximize2 className="w-3 h-3" /> Open
                    </span>
                  </button>
                )}

                {isActive && s.kind === 'ai_reflect' && (
                  <div className="mt-3 rounded-xl bg-white border border-fuchsia-100 p-3" data-testid="guided-reflection">
                    {session.reflection ? (
                      <div className="space-y-2">
                        <p className="font-heading text-sm font-bold text-slate-900 flex items-center gap-1.5">
                          <Trophy className="w-4 h-4 text-amber-500" /> {session.reflection.headline}
                        </p>
                        {(session.reflection.wins || []).map((w, wi) => (
                          <p key={wi} className="text-[12px] text-slate-600 font-body flex items-start gap-1.5">
                            <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> {w}
                          </p>
                        ))}
                        {(session.reflection.focus || []).length > 0 && (
                          <p className="text-[12px] text-rose-600 font-body">Revise next: {session.reflection.focus.join(', ')}</p>
                        )}
                        <p className="text-[12px] text-slate-700 font-body flex items-start gap-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-fuchsia-500 mt-0.5 flex-shrink-0" /> {session.reflection.tomorrow}
                        </p>
                        <p className="text-[11px] text-slate-400 font-body italic">{session.reflection.encouragement}</p>
                      </div>
                    ) : (
                      <p className="flex items-center gap-2 text-xs text-slate-400 font-body">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking back at today…
                      </p>
                    )}
                  </div>
                )}

                {done && s.key === 'quiz' && session.mastery_pct != null && (
                  <p className={`mt-2 text-[11px] font-heading ${session.mastery_pct >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}
                    data-testid="guided-mastery">
                    Mastery {session.mastery_pct}% {session.mastery_pct >= 80 ? '· chapter locked in' : '· added a revision step for you'}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* clock + Study Buddy's nudge */}
        <div className="space-y-2.5 lg:sticky lg:top-4 lg:self-start">
          <StudyTimer sessionId={session.session_id} paused={session.completed} onBreakChange={setOnBreak} />
          <div className="rounded-2xl bg-white ring-1 ring-fuchsia-100 px-3 py-2.5" data-testid="guided-buddy-nudge">
            <p className="font-heading text-[10px] uppercase tracking-widest text-fuchsia-500">Study Buddy</p>
            <p className="font-body text-[12px] text-slate-700 mt-1 leading-relaxed">
              {onBreak
                ? "Break first — I'm not going anywhere. Come back in five and we'll finish this chapter together."
                : nudge || (active
                  ? `Next up: ${active.title.toLowerCase()}. ${active.hint}`
                  : 'Chapter done — that was a proper session.')}
            </p>
          </div>
        </div>
      </div>

      {session.completed && (
        <p className="mt-3 text-center text-[12px] font-heading text-emerald-600 flex items-center justify-center gap-1.5"
          data-testid="guided-complete">
          <Play className="w-3.5 h-3.5" /> Session complete — see you tomorrow for the next chapter.
        </p>
      )}

      {overlay}
    </div>
  );
}

export default GuidedSessionPanel;
